import { MatchingWeekCategory } from '../db/category.js';
import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getMatchingWeekCategoriesByIDs = registerQuery({
  operationName: 'getMatchingWeekCategoriesByIDs',
  type: () => [MatchingWeekCategory],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<MatchingWeekCategory>>(gqlFields: SelSet) =>
      readEntitiesByIDs(MatchingWeekCategory, ids, gqlFields),
});

export const subscribeMatchingWeekCategoriesByIDs = registerSubscription({
  operationName: 'subscribeMatchingWeekCategoriesByIDs',
  type: () => [MatchingWeekCategory],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<MatchingWeekCategory>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(MatchingWeekCategory, ids, gqlFields),
});

export const getAllMatchingWeekCategories = registerQuery({
  operationName: 'getAllMatchingWeekCategories',
  type: () => [MatchingWeekCategory],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<MatchingWeekCategory>>(gqlFields: SelSet) =>
      readAllEntities(MatchingWeekCategory, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllMatchingWeekCategories = registerSubscription({
  operationName: 'subscribeAllMatchingWeekCategories',
  type: () => [MatchingWeekCategory],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<MatchingWeekCategory>>(gqlFields: SelSet) =>
      watchAllEntities(MatchingWeekCategory, gqlFields, { skip, limit, excludeIDs }),
});

export const getMatchingWeekCategoryByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<MatchingWeekCategory>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getMatchingWeekCategoriesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeMatchingWeekCategoryByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<MatchingWeekCategory>>(gqlFields: SelSet) =>
    id ? subscribeMatchingWeekCategoriesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
